import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/**
 * Quién prendió el candado de sesión. El sidebar y el bottom nav bloquean con
 * las TRES (es "hay una sesión abierta, no te salgas"); el candado de Ajustes
 * mira sólo `EXAM`, porque un examen propio en vuelo es lo único que no admite
 * que el usuario se vaya a cambiar idioma o plan a media hoja.
 */
export const EXAM_LOCK_SOURCE = {
  EXAM: 'exam',
  ADAPTIVE: 'adaptive',
  FLASHCARDS: 'flashcards',
  // Red de seguridad: una superficie futura que prenda el candado sin declararse
  // bloquea la navegación igual que hoy, pero NO se lleva Ajustes de corbata.
  OTHER: 'other',
};

// El copy del bloqueo vive UNA vez, como TABLA de claves i18n por superficie.
// Antes era una sola cadena en inglés hardcodeada aquí, y eso tenía dos bugs de
// un tirón: no se traducía, y hablaba de "tu examen" aunque el bloqueo viniera
// de una sesión de adaptive o de flashcards.
//
// Las claves viven en el namespace `common` (public/locales/{en,es}/common.json,
// grupo `toasts`), que es donde el repo ya guarda los toasts compartidos y los
// strings de navegación. Cambiar cualquier cosa bajo public/locales/ obliga a
// subir el `v` de i18n.js — sin eso el navegador sirve el JSON viejo y el
// usuario ve la clave cruda.
const NAV_BLOCKED_KEYS = {
  [EXAM_LOCK_SOURCE.EXAM]: 'toasts.nav_blocked.exam',
  [EXAM_LOCK_SOURCE.ADAPTIVE]: 'toasts.nav_blocked.adaptive',
  [EXAM_LOCK_SOURCE.FLASHCARDS]: 'toasts.nav_blocked.flashcards',
};

/**
 * Clave del aviso de bloqueo para una superficie. Una superficie desconocida (o
 * la de respaldo, OTHER) cae en la variante de adaptive, que es la genérica de
 * "sesión de estudio": es la única de las tres que no le miente al usuario
 * cuando no sabemos con certeza en qué estaba.
 */
export function navBlockedKey(source) {
  return NAV_BLOCKED_KEYS[source] || NAV_BLOCKED_KEYS[EXAM_LOCK_SOURCE.ADAPTIVE];
}

const ExamContext = createContext({
  isExamActive: false,
  isOwnExamActive: false,
  examLockSource: null,
  setExamActive: () => {},
});

export function ExamProvider({ children }) {
  // El estado pasó de booleano a REGISTRO de quién lo prendió. `isExamActive` se
  // deriva de él, así que para el sidebar y el bottom nav no cambia nada: sigue
  // siendo verdadero con exam, adaptive o flashcards, y sigue apagándose con
  // cualquier setExamActive(false) venga de donde venga (último que escribe,
  // manda — igual que con el booleano).
  const [source, setSource] = useState(null);
  // useCallback con deps vacías: la identidad DEBE ser estable. Media app tiene
  // a setExamActive en arreglos de dependencias de efectos (ExamSimulator,
  // AdaptiveStudy, Flashcards); una identidad nueva por render los relanzaría en
  // bucle. El setter de useState era estable y esto lo conserva.
  const setExamActive = useCallback((active, from = EXAM_LOCK_SOURCE.OTHER) => {
    setSource(active ? from : null);
  }, []);
  const value = {
    isExamActive: source !== null,
    isOwnExamActive: source === EXAM_LOCK_SOURCE.EXAM,
    examLockSource: source,
    setExamActive,
  };
  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
}

export function useExam() {
  return useContext(ExamContext);
}

/**
 * Single source of truth for the mid-session navigation lock.
 *
 * The sidebar has always locked its items while a study/exam session is active
 * (isExamActive); the mobile bottom nav must obey the SAME state with the SAME
 * blocked handler — this hook is the one place both read it from, so the two
 * surfaces can never drift. El copy sale de NAV_BLOCKED_KEYS, resuelto por i18n
 * y por la superficie que prendió el candado (antes era una sola cadena en
 * inglés que hablaba de "tu examen" pasara lo que pasara).
 */
export function useExamNavLock() {
  const { isExamActive, examLockSource } = useExam();
  const { t } = useTranslation('common');
  const blockNavigation = (e) => {
    // El guard de `e` es para quien no viene de un evento (el guard de ruta
    // dispara el mismo toast sin nada que prevenir). Las dos superficies de
    // navegación siempre pasan su evento: para ellas no cambia nada.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // El aviso habla de la superficie que REALMENTE prendió el candado, no
    // siempre del examen.
    toast.info(t(navBlockedKey(examLockSource)));
  };
  return { isExamActive, blockNavigation };
}

/**
 * Candado de los Ajustes de la app (T1-C3).
 *
 * Deriva de la MISMA señal que la cerradura de navegación, pero filtrada al
 * examen propio: adaptive y flashcards jamás bloquean Ajustes. No guarda estado
 * propio, así que al entregar o vencerse el examen se levanta solo.
 */
export function useSettingsExamLock() {
  const { isOwnExamActive } = useExam();
  const { t } = useTranslation('common');
  const blockSettings = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // Esta cerradura sólo se arma con examen propio, así que su variante es
    // siempre la de examen. Se resuelve por la MISMA tabla que la de navegación
    // (no por una cadena aparte) para que las dos no puedan divergir.
    toast.info(t(navBlockedKey(EXAM_LOCK_SOURCE.EXAM)));
  };
  return { isSettingsLocked: isOwnExamActive, blockSettings };
}
